import React, { useState } from 'react';
import { Eye, SkipForward, Shuffle, Trophy, Lightbulb, Plus, Check } from 'lucide-react';
import { GUESS_IT_ITEMS, GUESS_CATEGORY_COLORS } from '../../data/guessItItems';
import { TEAM_META, TEAM_IDS, shuffle } from '../../data/teams';
import GameOver from './GameOver';

// Points for guessing after N clues are showing: 1 clue → 4 pts … 4 clues → 1.
const pointsFor = (revealed) => Math.max(1, 5 - revealed);

export default function GuessIt({ scores, onAward, onResetScores }) {
  const [deck, setDeck] = useState(() => shuffle(GUESS_IT_ITEMS));
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(1);   // how many clues are showing (starts at 1)
  const [answerShown, setAnswerShown] = useState(false);

  const finished = index >= deck.length;
  const current = deck[index];

  const goNext = () => { setRevealed(1); setAnswerShown(false); setIndex((x) => x + 1); };
  const award = (teamId) => { onAward(teamId, pointsFor(revealed)); goNext(); };
  const restart = () => {
    setDeck(shuffle(GUESS_IT_ITEMS));
    setIndex(0);
    setRevealed(1);
    setAnswerShown(false);
    onResetScores();
  };

  if (finished) return <GameOver scores={scores} onRestart={restart} />;

  const catColor = GUESS_CATEGORY_COLORS[current.category] || '#475569';
  const value = pointsFor(revealed);
  const allCluesOut = revealed >= current.clues.length;
  const progress = Math.round((index / deck.length) * 100);

  return (
    <div className="space-y-4">
      {/* Controls row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm font-semibold text-slate-500">
          Riddle <span className="text-slate-900">{index + 1}</span> of {deck.length}
        </div>
        <button
          onClick={restart}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border bg-white text-slate-600 border-slate-200 hover:bg-slate-50 transition"
          title="Reshuffle the riddles and reset scores"
        >
          <Shuffle size={13} /> Restart
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: 'var(--accent)' }} />
      </div>

      {/* Riddle card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center justify-between gap-2 mb-5">
          <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ color: catColor, background: `${catColor}14` }}>
            {current.category}
          </span>
          <span
            className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full"
            style={{ color: 'var(--accent)', background: 'rgba(249,101,22,0.10)' }}
            title="Points if a team guesses now"
          >
            <Trophy size={12} /> Worth {value} pt{value === 1 ? '' : 's'}
          </span>
        </div>

        {/* Clues revealed so far */}
        <ol className="space-y-2.5">
          {current.clues.slice(0, revealed).map((clue, i) => (
            <li key={i} className="flex items-start gap-3 animate-fadeIn">
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 mt-0.5"
                style={{ background: catColor }}
              >
                {i + 1}
              </span>
              <span className="text-base sm:text-lg text-slate-800 font-medium">{clue}</span>
            </li>
          ))}
        </ol>

        {/* Answer */}
        {answerShown && (
          <div className="mt-5 inline-flex items-center gap-2 text-xl sm:text-2xl font-extrabold font-display text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 animate-fadeIn">
            <Check size={20} /> {current.answer}
          </div>
        )}

        {/* Reveal controls */}
        <div className="mt-6 flex flex-wrap gap-2">
          {!allCluesOut && !answerShown && (
            <button
              onClick={() => setRevealed((r) => r + 1)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition"
            >
              <Lightbulb size={15} /> Reveal next clue ({current.clues.length - revealed} left)
            </button>
          )}
          {!answerShown && (
            <button
              onClick={() => setAnswerShown(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              <Eye size={15} /> Reveal answer
            </button>
          )}
        </div>
      </div>

      {/* Award / skip */}
      <div className="space-y-2">
        <div className="text-center text-xs font-semibold uppercase tracking-wider text-slate-400">
          Award {value} point{value === 1 ? '' : 's'} to the team that guessed
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TEAM_IDS.map((id) => (
            <button
              key={id}
              onClick={() => award(id)}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white transition hover:opacity-90"
              style={{ background: TEAM_META[id].color }}
            >
              <Plus size={16} />{value} {TEAM_META[id].name}
            </button>
          ))}
        </div>
        <button
          onClick={goNext}
          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-500 hover:bg-slate-50 transition"
        >
          <SkipForward size={15} /> No one — next riddle
        </button>
      </div>
    </div>
  );
}
